import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/I18nContext";
import translations from "../../i18n/translations";
import LeadModal from "../../pages/home/LeadModal";
import { getHomeContent } from "../../pages/home/homeContent";
import "./Header.css";

function Header({
  showSidebarToggle = false,
  sidebarOpen = false,
  onToggleSidebar,
}) {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { t, language, setLanguage, languages } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [leadOpen, setLeadOpen] = useState(false);
  const closeLead = useCallback(() => setLeadOpen(false), []);
  const isLoggedIn = Boolean(user);
  const navLinks = useMemo(
    () => [
      { to: "/", label: t("nav.home"), end: true },
      { to: "/courses", label: t("nav.courses") },
      { to: "/about", label: t("nav.about") },
      { to: "/contact", label: t("nav.contact") },
    ],
    [t],
  );

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const query = new FormData(event.currentTarget).get("q");
    if (!query) {
      return;
    }
    navigate(`/courses?search=${encodeURIComponent(query)}`);
    event.currentTarget.reset();
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const headerClass = `site-header${isScrolled ? " is-scrolled" : ""}`;

  if (isLoggedIn) {
    return (
      <header className={headerClass}>
        <nav className="main-nav">
          <div className="container nav-container">
            {showSidebarToggle && (
              <button
                type="button"
                className={`nav-sidebar-toggle${sidebarOpen ? " is-active" : ""}`}
                aria-expanded={sidebarOpen}
                aria-controls="app-sidebar"
                onClick={onToggleSidebar}
              >
                <span className="sr-only">{t("sidebar.toggle")}</span>
                <svg
                  className="nav-sidebar-toggle__icon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M4 6h16M4 12h16M4 18h16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            <div className="nav-actions nav-actions--desktop nav-lang">
              <label htmlFor="lang-select-logged" className="sr-only">
                {t("nav.languageLabel")}
              </label>
              <select
                id="lang-select-logged"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                {languages.map((code) => (
                  <option key={code} value={code}>
                    {translations[code]?.meta?.name || code}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </nav>
      </header>
    );
  }

  const renderNavLinks = (onNavigate) =>
    navLinks.map(({ to, label, end }) => (
      <NavLink key={to} to={to} end={end} onClick={onNavigate}>
        {label}
      </NavLink>
    ));

  return (
    <header className={headerClass}>
      <nav className="main-nav">
        <div className="container nav-container">
          <Link
            to="/"
            className="nav-brand nav-brand--mobile"
            onClick={() => setMenuOpen(false)}
          >
            <img src="/logo.png" alt="Casaclass" className="nav-logo" />
          </Link>

          <div className="nav-desktop">
            <div className="nav-actions nav-actions--desktop">
              <div className="nav-lang">
                <label htmlFor="lang-select" className="sr-only">
                  {t("nav.languageLabel")}
                </label>
                <select
                  id="lang-select"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  {languages.map((code) => (
                    <option key={code} value={code}>
                      {translations[code]?.meta?.name || code}
                    </option>
                  ))}
                </select>
              </div>
              {!loading && (
                <>
                  <Link to="/login" className="btn ghost">
                    {t("auth.login")}
                  </Link>
                  <Link to="/signup" className="btn primary">
                    {t("auth.signup")}
                  </Link>
                  <button
                    type="button"
                    className="btn nav-free-session"
                    onClick={() => setLeadOpen(true)}
                  >
                    {getHomeContent(language).cta.short}
                  </button>
                </>
              )}
            </div>

            <form
              className="nav-search nav-search--desktop"
              role="search"
              onSubmit={handleSearchSubmit}
            >
              <label htmlFor="header-search" className="sr-only">
                {t("nav.searchLabel")}
              </label>
              <input
                id="header-search"
                type="search"
                name="q"
                placeholder={t("nav.searchPlaceholder")}
                autoComplete="off"
              />
              <button type="submit" aria-label={t("nav.searchLabel")}>
                <i
                  className="fa-solid fa-magnifying-glass"
                  aria-hidden="true"
                ></i>
              </button>
            </form>

            <div className="nav-left">
              <Link to="/" className="nav-brand nav-brand--desktop">
                <img src="/logo.png" alt="Casaclass" className="nav-logo" />
              </Link>
              <div className="nav-links">{renderNavLinks(undefined)}</div>
            </div>
          </div>

          <button
            className={`nav-toggle${menuOpen ? " is-active" : ""}`}
            type="button"
            aria-expanded={menuOpen}
            aria-controls="primary-navigation"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="sr-only">{t("nav.menuLabel")}</span>
            <svg
              className="nav-toggle__icon"
              viewBox="0 0 24 24"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div
            className={`nav-panel${menuOpen ? " is-open" : ""}`}
            id="primary-navigation"
          >
            <div className="nav-links">
              {renderNavLinks(() => setMenuOpen(false))}
            </div>
            <form
              className="nav-search nav-search--mobile"
              role="search"
              onSubmit={handleSearchSubmit}
            >
              <label htmlFor="header-search-mobile" className="sr-only">
                {t("nav.searchLabel")}
              </label>
              <input
                id="header-search-mobile"
                type="search"
                name="q"
                placeholder={t("nav.searchPlaceholder")}
                autoComplete="off"
              />
              <button type="submit" aria-label={t("nav.searchLabel")}>
                <i
                  className="fa-solid fa-magnifying-glass"
                  aria-hidden="true"
                ></i>
              </button>
            </form>
            <div className="nav-actions nav-actions--mobile">
              {!loading && (
                <>
                  <Link
                    to="/login"
                    className="btn ghost"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("auth.login")}
                  </Link>
                  <Link
                    to="/signup"
                    className="btn primary"
                    onClick={() => setMenuOpen(false)}
                  >
                    {t("auth.signup")}
                  </Link>
                  <button
                    type="button"
                    className="btn nav-free-session"
                    onClick={() => {
                      setMenuOpen(false);
                      setLeadOpen(true);
                    }}
                  >
                    {getHomeContent(language).cta.short}
                  </button>
                </>
              )}
              <div className="nav-lang nav-lang--mobile">
                <label htmlFor="lang-select-mobile" className="sr-only">
                  {t("nav.languageLabel")}
                </label>
                <select
                  id="lang-select-mobile"
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                >
                  {languages.map((code) => (
                    <option key={code} value={code}>
                      {translations[code]?.meta?.name || code}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <LeadModal open={leadOpen} onClose={closeLead} ctaSource="header" />
    </header>
  );
}

export default Header;
